import { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useConfirm } from '../context/ConfirmContext';
import { InvoiceIssuerForm } from '../components/InvoiceIssuerForm';
import { getDefaultInvoiceIssuer, normalizeInvoiceIssuer, pickIssuerPayload } from '../constants/invoiceIssuer';
import { PageHeader } from '../components/ui/PageHeader';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { StatCard } from '../components/ui/StatCard';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { API_BASE_URL } from '../api';
import { Activity, BookOpen, CircleHelp, ExternalLink, FileText, RefreshCw, Trash2, Users } from 'lucide-react';
import { DELETE_DATA_URL, HELP_URL, PRIVACY_URL, TERMS_URL } from '../constants/contact';
import { openExternalUrl } from '../utils/openExternalUrl';
import { ThemeToggle } from '../components/ui/ThemeToggle';

function isSchoolHeadAccount(head) {
  return head.account_role !== 'admin' && head.username !== 'admin';
}

export function AdminSettings() {
  const {
    labels,
    schoolHeads,
    fetchSchoolHeads,
    isLoading,
    useMalayalam,
    setUseMalayalam,
    invoiceIssuer,
    fetchInvoiceIssuer,
    saveInvoiceIssuer,
  } = useApp();
  const { confirm: confirmDialog } = useConfirm();
  const [apiStatus, setApiStatus] = useState('checking');
  const [issuer, setIssuer] = useState(() => normalizeInvoiceIssuer(invoiceIssuer));

  const heads = schoolHeads.filter(isSchoolHeadAccount);
  const activeCount = heads.filter((h) => !h.paused).length;
  const pausedCount = heads.length - activeCount;
  const totalDivisions = heads.reduce((sum, h) => sum + (parseInt(h.divisions, 10) || 0), 0);

  const checkApi = async () => {
    setApiStatus('checking');
    try {
      const res = await fetch(`${API_BASE_URL}/health`, { method: 'GET' });
      setApiStatus(res.ok ? 'online' : 'error');
    } catch {
      setApiStatus('offline');
    }
  };

  useEffect(() => {
    checkApi();
  }, []);

  useEffect(() => {
    fetchInvoiceIssuer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIssuer(normalizeInvoiceIssuer(invoiceIssuer));
  }, [invoiceIssuer]);

  const handleSaveIssuer = async () => {
    const saved = await saveInvoiceIssuer(pickIssuerPayload(issuer));
    if (saved) setIssuer(normalizeInvoiceIssuer(saved));
  };

  const handleResetIssuer = async () => {
    const ok = await confirmDialog(labels.invoiceResetIssuer + '?');
    if (!ok) return;
    const defaults = getDefaultInvoiceIssuer();
    const saved = await saveInvoiceIssuer(pickIssuerPayload(defaults));
    if (saved) setIssuer(normalizeInvoiceIssuer(saved));
  };

  const statusBadge =
    apiStatus === 'online' ? (
      <Badge variant="success">{labels.apiOnline}</Badge>
    ) : apiStatus === 'checking' ? (
      <Badge variant="default">{labels.loading}</Badge>
    ) : (
      <Badge variant="warning">{labels.apiOffline}</Badge>
    );

  return (
    <div className="space-y-6">
      <PageHeader title={labels.settingsTitle} subtitle={labels.settingsSubtitle} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label={labels.schoolHeads} value={heads.length} />
        <StatCard label={labels.active} value={activeCount} />
        <StatCard label={labels.paused} value={pausedCount} />
        <StatCard label={labels.totalDivisionsCap} value={totalDivisions} sub={labels.totalDivisionsCapHint} />
      </div>

      <Card>
        <CardHeader
          title={labels.systemStatusTitle}
          action={
            <Button variant="secondary" size="sm" onClick={checkApi}>
              <RefreshCw className="h-4 w-4" />
              {labels.refresh}
            </Button>
          }
        />
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-surface px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-text">
              <Activity className="h-4 w-4 text-muted" />
              <span>{labels.apiConnection}</span>
            </div>
            {statusBadge}
          </div>
          <p className="text-xs text-muted break-all">{API_BASE_URL}</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fetchSchoolHeads()}
            isLoading={isLoading.heads}
          >
            <Users className="h-4 w-4" />
            {labels.refreshSchoolHeads}
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={labels.invoiceIssuerDetailsTitle} />
        <CardBody className="space-y-4">
          <p className="text-xs text-muted">{labels.invoiceIssuerDetailsHint}</p>
          <InvoiceIssuerForm labels={labels} issuer={issuer} onChange={setIssuer} />
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleSaveIssuer} isLoading={isLoading.invoiceIssuer}>
              {labels.save}
            </Button>
            <Button variant="secondary" onClick={handleResetIssuer} disabled={isLoading.invoiceIssuer}>
              {labels.invoiceResetIssuer}
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={labels.preferencesTitle} />
        <CardBody className="space-y-3">
          <div className="rounded-lg border border-border px-4 py-3 space-y-3">
            <div>
              <p className="text-sm font-medium text-text">{labels.appearance}</p>
              <p className="text-xs text-muted">{labels.appearanceHint}</p>
            </div>
            <ThemeToggle useMalayalam={useMalayalam} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border px-4 py-3">
            <div>
              <p className="text-sm font-medium text-text">{labels.appLanguage}</p>
              <p className="text-xs text-muted">{labels.appLanguageHint}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setUseMalayalam(!useMalayalam)}>
              {labels.toggleLang}
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={labels.howItWorksTitle} />
        <CardBody className="space-y-4">
          <ol className="space-y-3 text-sm text-text">
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-light text-xs font-semibold text-primary">1</span>
              <span>{labels.howItWorksAdmin}</span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-light text-xs font-semibold text-primary">2</span>
              <span>{labels.howItWorksHead}</span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-light text-xs font-semibold text-primary">3</span>
              <span>{labels.howItWorksTeacher}</span>
            </li>
          </ol>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={labels.resourcesTitle} />
        <CardBody className="space-y-2">
          <a
            href="https://nmhelper.in"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between rounded-lg border border-border px-4 py-3 text-sm font-medium text-primary hover:bg-surface"
          >
            {labels.websiteLink}
            <ExternalLink className="h-4 w-4" />
          </a>
          <a
            href={HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              openExternalUrl(HELP_URL);
            }}
            className="flex items-center justify-between rounded-lg border border-border px-4 py-3 text-sm font-medium text-text hover:bg-surface"
          >
            <span className="flex items-center gap-2">
              <CircleHelp className="h-4 w-4 text-muted" />
              {labels.helpLink}
            </span>
            <ExternalLink className="h-4 w-4 text-muted" />
          </a>
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              openExternalUrl(TERMS_URL);
            }}
            className="flex items-center justify-between rounded-lg border border-border px-4 py-3 text-sm font-medium text-text hover:bg-surface"
          >
            <span className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted" />
              {labels.termsLink}
            </span>
            <ExternalLink className="h-4 w-4 text-muted" />
          </a>
          <a
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              openExternalUrl(PRIVACY_URL);
            }}
            className="flex items-center justify-between rounded-lg border border-border px-4 py-3 text-sm font-medium text-text hover:bg-surface"
          >
            <span className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-muted" />
              {labels.privacyLink}
            </span>
            <ExternalLink className="h-4 w-4 text-muted" />
          </a>
          <a
            href={DELETE_DATA_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              openExternalUrl(DELETE_DATA_URL);
            }}
            className="flex items-center justify-between rounded-lg border border-border px-4 py-3 text-sm font-medium text-text hover:bg-surface"
          >
            <span className="flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-muted" />
              {labels.deleteDataLink}
            </span>
            <ExternalLink className="h-4 w-4 text-muted" />
          </a>
        </CardBody>
      </Card>
    </div>
  );
}
